import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from './CreateDerivedView.module.scss';
import Header from 'component/layout/header/Header';
import Container from 'component/atom/container/Container';
import Input from 'component/atom/forms/input/input/Input';
import Button from 'component/atom/forms/button/Button';
import TextArea from 'component/atom/forms/text-area/TextArea';
import TableComponent from 'component/molecules/Table/TableComponent';
import { useNavigate, useParams } from 'react-router-dom';
import { usePost } from 'hooks/usePost';
import { derivedViews } from 'hooks/endpoints';
import { useFetch } from 'hooks/useFetch';

const uuidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const validationSchema = Yup.object().shape({
  agentId: Yup.string()
    .required('agentId is required')
    .transform((v) => (typeof v === 'string' ? v.trim() : v))
    .test('agentId-uuid', 'Invalid agentId (must be a UUID)', (value) =>
      uuidRegex.test(value || '')
    ),

  name: Yup.string()
    .required('Name is required')
    .transform((v) => (typeof v === 'string' ? v.trim() : v)),

  nlText: Yup.string().notRequired(),

  sqlQuery: Yup.string()
    .required('SQL Query is required')
    .transform((v) => (typeof v === 'string' ? v.trim() : v)),
});

const CreateDerivedView = () => {
  const { id, agentId } = useParams();
  const navigate = useNavigate();
  const { mutate: postDerivedView, isPending } = usePost({
    onErrorToast: 'Something went wrong',
    onSuccessToast: 'Created Successfully',
    toastType: 'add',
  });

  const { data, refetch } = useFetch(
    ['singleView', id],
    derivedViews.fetchDerivedViewById,
    {
      enable: false,
    },
    {
      derivedViewId: id,
    }
  );

  useEffect(() => {
    if (id) {
      refetch();
    }
  }, [id]);

  const tableData = [
    {
      id: 'STU002',
      body: '--',
      url: '--',
      grade: '--',
      score: '--',
      submittedAt: '2023-09-01',
      assignmentId: 'A123',
      attendance: '45%',
      quiz1: '55',
    },
  ];

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: id || '',
      agentId: agentId || '',
      name: data?.data?.name || '',
      nlText: data?.data?.nlText || '',
      sqlQuery: data?.data?.sqlQuery || '',
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: (values) => {
      const payload = {
        id: values.id || undefined,
        agentId: values.agentId?.trim() || '',
        name: values.name?.trim() || '',
        nlText: values.nlText || '',
        sqlQuery: values.sqlQuery?.trim() || '',
      };

      if (!payload.id) delete payload.id;

      postDerivedView({
        url: derivedViews.createUpdateDerivedView,
        data: payload,
        requireAuth: true,
      });
    },
  });

  return (
    <>
      <Header
        title={id ? 'Update Derived View' : 'Create Derived View'}
        isBack
        isSub
      />

      <form onSubmit={formik.handleSubmit}>
        <Container className={styles.cont}>
          <div className={styles['top-section']}>
            <div style={{ width: '100%' }}>
              <Input
                label='Derived View Name'
                placeholder='Enter Name'
                inputClassname={styles['search-input']}
                name='name'
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.name}
              />
            </div>

            <div className={styles['actions']}>
              <Button
                label='Cancel'
                variant='dark'
                onClick={(e) => {
                  e?.preventDefault?.();
                  formik.resetForm();
                }}
              />
              <Button
                label='Save'
                variant='primary'
                type='save'
                onClick={(e) => {
                  e?.preventDefault?.();
                  formik.handleSubmit();
                }}
                disabled={isPending}
                isLoading={isPending}
              />
            </div>
          </div>

          <div className={styles['form-container']}>
            <div className={styles['steps-container']}>
              <div className={styles['card']}>
                <p className={styles['step-title']}>
                  Step 1: Describe Your Criteria
                </p>
                <p className={styles['step-description']}>
                  Describe what you're looking for in natural language. For
                  example: "I want attendance to be below 70% and quiz 1 score
                  to be below 60%"
                </p>

                <TextArea
                  label='AI Description'
                  placeholder='Enter Description'
                  wrapperClassName={styles['ai-textarea']}
                  name='nlText'
                  value={formik.values.nlText}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />

                <Button
                  label='Generate SQL'
                  type='sql'
                  className={styles['sql-btn']}
                  onClick={(e) => e?.preventDefault?.()}
                />
              </div>

              <div className={styles['card']}>
                <p className={styles['step-title']}>
                  Step 2: Review & Edit SQL
                </p>
                <p className={styles['step-description']}>
                  Review the generated SQL query. You can edit it if needed
                  before applying.
                </p>

                <TextArea
                  label='SQL Query'
                  placeholder='Enter Query'
                  wrapperClassName={styles['ai-textarea']}
                  name='sqlQuery'
                  value={formik.values.sqlQuery}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />

                <Button
                  label='Apply SQL'
                  className={styles['sql-btn']}
                  onClick={(e) => e?.preventDefault?.()}
                />
              </div>
            </div>

            <div className={styles['card']}>
              <div className={styles['table-header']}>
                <div className={styles['title-container']}>
                  <p className={styles['table-title']}>Step 3: View Results</p>
                  <p className={styles['table-description']}>
                    Custom table (derived view) based on your criteria
                  </p>
                </div>
                <Button
                  label='Export CSV'
                  type='download'
                  onClick={(e) => e?.preventDefault?.()}
                />
              </div>

              <div className={styles['records']}>
                <p>10 flagged records</p>
              </div>

              <TableComponent
                rowKey='text'
                tableContainerClassName={styles['table-container']}
                headerData={{
                  tableName: 'Derived Views',
                  hasExport: false,
                  hasSearch: false,
                  hasDelete: false,
                  hasEdit: false,
                  hasPaging: true,
                  hasCheckbox: false,
                  hasUploadCSV: false,
                  hasFilter: false,
                  isExpandable: false,
                  fields: [
                    { fieldName: 'id', headerName: 'ID' },
                    { fieldName: 'body', headerName: 'Body' },
                    { fieldName: 'url', headerName: 'URL' },
                    { fieldName: 'grade', headerName: 'Grade' },
                    { fieldName: 'score', headerName: 'Score' },
                    { fieldName: 'submittedAt', headerName: 'SubmittedAt' },
                    { fieldName: 'assignmentId', headerName: 'Assignment ID' },
                    { fieldName: 'attendance', headerName: 'Attendance' },
                    { fieldName: 'quiz1', headerName: 'Quiz 1 Score' },
                  ],
                }}
                externalData={tableData}
              />
            </div>
          </div>
        </Container>
      </form>
    </>
  );
};

export default CreateDerivedView;
